import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/lib/auth";

/**
 * /signup — a deliberately stripped-down landing for external marketing
 * pages to link directly to. No hero content, no listener wall, no fold —
 * just the trial promise and a single Google-sign-in CTA. Preserves
 * ?ref=CODE for partner attribution the same way Landing.jsx does.
 *
 * If a user is already signed in, we bounce them straight to /dashboard so
 * a bookmarked signup link doesn't awkwardly stall the returning visitor.
 */
export default function Signup() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  useEffect(() => {
    // Capture affiliate ref so it survives the Google OAuth round-trip.
    // Same key Landing.jsx uses — server reads it on session creation.
    try {
      const ref = search.get("ref");
      if (ref) {
        window.sessionStorage.setItem("umbra_ref", ref.trim().toLowerCase());
      }
    } catch (e) {
      // sessionStorage disabled — signup will still work, just unattributed.
    }
  }, [search]);

  // Signed-in users get bounced to their dashboard so a stale signup link
  // doesn't dead-end them on this page.
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian-900 text-bone-100 px-6 py-16">
      <div className="w-full max-w-md text-center">
        <p
          className="font-sans uppercase tracking-[0.32em] text-[0.65rem] text-terracotta mb-4"
          data-testid="signup-eyebrow"
        >
          Umbra · a voice shadow journal
        </p>
        <h1
          className="font-serif text-3xl sm:text-4xl leading-[1.15] text-bone-100 mb-4"
          data-testid="signup-headline"
        >
          Start your <em className="text-terracotta not-italic">7-day free trial.</em>
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-300 leading-relaxed mb-10">
          Full workbook access, unhurried. When the week ends, Chapter&nbsp;1
          stays open forever &mdash; the rest quietly settles behind a door
          until you choose a tempo.
        </p>

        <Button
          onClick={startGoogleLogin}
          disabled={loading}
          data-testid="signup-google-btn"
          className="w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6 px-8 min-h-[56px] flex items-center justify-center gap-3 group"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Sign up with Google
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>

        <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mt-6">
          No credit card · Cancel anytime · Chapter 1 free forever
        </p>

        <p className="font-serif italic text-sm text-bone-500 mt-10 leading-relaxed">
          Already a listener?{" "}
          <button
            type="button"
            onClick={startGoogleLogin}
            data-testid="signup-signin-link"
            className="text-bone-300 hover:text-terracotta underline underline-offset-4"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
